/**
 * Catálogo do bloqueio de emissão — porta `cteatf_bloqueio.dart`.
 * A porta única do "pode emitir?": todo ponto que decide isso lê daqui.
 * Ordem deliberada: bloqueio de conta (inadimplência) antes de crédito.
 */

export type CausaBloqueio = 'inadimplencia' | 'credito' | 'sessao' | 'operacional';

export interface BloqueioEmissao {
  causa: CausaBloqueio;
  titulo: string;
  mensagem: string;
  rotuloAcao: string;
}

/**
 * Classifica o motivo livre que `/atf/elegibilidade` devolve. Motivo vazio
 * ou desconhecido cai em inadimplência de propósito — errar para o lado do
 * crédito mandaria pagar uma recarga que não resolve.
 */
export function causaDoMotivo(motivo: string | null | undefined): CausaBloqueio {
  const m = (motivo ?? '').toLowerCase();
  if (m.includes('fatura') || m.includes('inadimpl') || m.includes('vencid')) return 'inadimplencia';
  if (m.includes('crédito') || m.includes('credito') || m.includes('recarg') || m.includes('saldo')) return 'credito';
  return 'inadimplencia';
}

/** 401/403 (sessão) vs. qualquer outro status ou ausência (operacional/rede). */
export function causaDaFalhaDeCarga(status: number | null | undefined): CausaBloqueio {
  return status === 401 || status === 403 ? 'sessao' : 'operacional';
}

export function tituloDoBloqueio(c: CausaBloqueio): string {
  switch (c) {
    case 'credito': return 'Sem crédito para emitir';
    case 'inadimplencia': return 'Emissão bloqueada';
    case 'sessao': return 'Sessão expirada';
    case 'operacional': return 'Emissão indisponível';
  }
}

export function rotuloDoBloqueio(c: CausaBloqueio): string {
  switch (c) {
    case 'credito': return 'Recarregar crédito';
    case 'inadimplencia': return 'Ver Minhas Faturas';
    case 'sessao': return 'Entrar novamente';
    case 'operacional': return 'Tentar novamente';
  }
}

/** A mensagem de crédito nunca menciona fatura. */
export function mensagemPadraoDoBloqueio(c: CausaBloqueio): string {
  switch (c) {
    case 'credito': return 'Você não tem crédito disponível para emitir. Recarregue para voltar a emitir ATF.';
    case 'inadimplencia': return 'Emissão bloqueada por inadimplência. Há faturas vencidas. Regularize para emitir novas ATFs.';
    case 'sessao': return 'Sessão expirada. Entre novamente para solicitar a ATF.';
    case 'operacional': return 'Não é possível solicitar ATF no momento.';
  }
}

export function bloqueioEmissaoDe({
  bloqueado, causaBloqueio, bloqueioMsg, exibeCredito, semCreditoParaEmitir, bloqueioCreditoMsg,
}: {
  bloqueado: boolean;
  causaBloqueio: CausaBloqueio | null;
  bloqueioMsg: string | null;
  exibeCredito: boolean;
  semCreditoParaEmitir: boolean;
  bloqueioCreditoMsg: string | null;
}): BloqueioEmissao | null {
  if (bloqueado) {
    const causa = causaBloqueio ?? 'operacional';
    return {
      causa, titulo: tituloDoBloqueio(causa),
      mensagem: bloqueioMsg ?? mensagemPadraoDoBloqueio(causa),
      rotuloAcao: rotuloDoBloqueio(causa),
    };
  }
  // `bloqueioCreditoMsg` cobre o 402 que chega no submit, quando o saldo
  // ainda parecia suficiente na entrada — segunda guarda, não substituta.
  if (exibeCredito && (semCreditoParaEmitir || bloqueioCreditoMsg != null)) {
    return {
      causa: 'credito', titulo: tituloDoBloqueio('credito'),
      mensagem: bloqueioCreditoMsg ?? mensagemPadraoDoBloqueio('credito'),
      rotuloAcao: rotuloDoBloqueio('credito'),
    };
  }
  return null;
}
